
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { MdVisibility, MdVisibilityOff, MdLockReset, MdArrowBack, MdSave } from 'react-icons/md';
import { CHANGE_PASSWORD_API } from '../config/API';
import { useUser } from '../context/UserContext';
import { showErrorToast } from '../utils/toastUtils';
import './ResetPassword.css';

const ResetPassword = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [passwords, setPasswords] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!passwords.oldPassword) {
            setError('Old password is required');
            return;
        }

        if (passwords.newPassword !== passwords.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (passwords.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            // Prepare the flat payload as requested
            const payload = {
                oldPassword: passwords.oldPassword,
                newPassword: passwords.newPassword
            };

            // Call Change Password API
            const response = await axios.put(
                CHANGE_PASSWORD_API,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: 'Password Updated',
                    text: 'Your password has been changed successfully.',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate('/'); // Redirect to dashboard or previous page
            } else {
                throw new Error(response.data.message || 'Failed to update password');
            }

        } catch (err) {
            console.error('Password reset error:', err);
            setError(err.message || 'An error occurred while updating password');
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="reset-password-page">
            <div className="page-header">
                <button onClick={() => navigate(-1)} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Reset Password</h2>
            </div>

            <div className="reset-password-container">
                <div className="reset-card">
                    <div className="reset-header">
                        <div className="icon-wrapper">
                            <MdLockReset size={32} />
                        </div>
                        <h3>Create New Password</h3>
                        <p>Your new password must be different from previous used passwords.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="reset-form">
                        <div className="form-group">
                            <label>Old Password</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="oldPassword"
                                    value={passwords.oldPassword}
                                    onChange={handleChange}
                                    placeholder="Enter old password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>New Password</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="newPassword"
                                    value={passwords.newPassword}
                                    onChange={handleChange}
                                    placeholder="Enter new password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Confirm Password</label>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={passwords.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirm new password"
                            />
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="form-actions">
                            <button type="button" className="cancel-btn" onClick={() => navigate(-1)}>
                                Cancel
                            </button>
                            <button type="submit" className="save-btn" disabled={loading}>
                                {loading ? 'Updating...' : <><MdSave /> Update Password</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
